import { useEffect, useState, useRef, useDeferredValue } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchProviders, editProvider, removeProvider } from 'modules/categories/categoriesSlice';
// eslint-disable-next-line max-len
import SurgicalProvidersComponent from 'modules/categories/surgical/components/SurgicalProvidersComponent';
import ConfirmationModal from 'modules/common/components/ConfirmationModal';
import { searchQueryMinLength, status } from 'constants/general';
import { useParams } from 'react-router-dom';

// ###############################################################
// <------------------------ Constants -------------------------->
// ###############################################################
// ###############################################################
// <---------------------- End Constants ------------------------>
// ###############################################################
const SurgicalProvidersContainer = () => {
  // ###############################################################
  // <------------------------- Globals --------------------------->
  // ###############################################################
  const dispatch = useDispatch();
  const { priceListId } = useParams();
  // ###############################################################
  // <----------------------- End Globals ------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------ useState --------------------------->
  // ###############################################################
  const [searchQuery, setSearchQuery] = useState('');
  const InitialQueryString = {
    priceListId,
    pageNumber: 1,
    pagesize: 10,
  };
  const [providersQueryString, setProvidersQueryString] = useState(InitialQueryString);
  const [isCreateProviderModalHidden, setIsCreateProviderModalHidden] = useState(true);
  const [filtersSelected, setFiltersSelected] = useState([]);
  const [isDeactivateConfirmationHidden, setIsDeactivateConfirmationHidden] = useState(true);
  const [isExclusiveConfirmationHidden, setIsExclusiveConfirmationHidden] = useState(true);
  const [isRemoveConfirmationHidden, setIsRemoveConfirmationHidden] = useState(true);
  const [pendingAction, setPendingAction] = useState(null);

  // ###############################################################
  // <---------------------- End useState ------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------ useEffect -------------------------->
  // ###############################################################
  const removeProviderState = useSelector(state => state.categories.removeProvider);
  const editProviderState = useSelector(state => state.categories.editProvider);
  const prevRemoveProviderState = useDeferredValue(removeProviderState.status);
  const prevEditProviderState = useDeferredValue(editProviderState.status);
  useEffect(() => {
    if (
      (status.SUCCESS === removeProviderState.status &&
        prevRemoveProviderState !== removeProviderState.status) ||
      (status.SUCCESS === editProviderState.status &&
        prevEditProviderState !== editProviderState.status)
    ) {
      setProvidersQueryString({ ...InitialQueryString });
    }
  }, [removeProviderState.status, editProviderState.status]);
  useEffect(() => {
    dispatch(fetchProviders(providersQueryString));
  }, [dispatch, providersQueryString]);
  // ###############################################################
  // <---------------------- End useEffect ------------------------>
  // ###############################################################
  // ###############################################################
  // <--------------------- rest of hooks ------------------------->
  // ###############################################################
  const searchBarInputTimeout = useRef(null);
  // ###############################################################
  // <-------------------- End rest of hooks ---------------------->
  // ###############################################################
  // ###############################################################
  // <-------------------------- Logic ---------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------ End Logic -------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------- Actions --------------------------->
  // ###############################################################
  // ###############################################################
  // <----------------------- End Actions ------------------------->
  // ###############################################################
  // ###############################################################
  // <----------------------- UI Handlers ------------------------->
  // ###############################################################
  const handleOnToggleAddProvidersModel = isHidden => {
    setIsCreateProviderModalHidden(isHidden);
    if (isHidden) setProvidersQueryString({ ...InitialQueryString });
  };

  const handleInputFieldChange = () => {};

  const handleSearchInputChange = (searchTerm, reset) => {
    const queryString = {
      ...providersQueryString,
      pageNumber: 1,
      isShowMore: false,
    };
    if (searchTerm) queryString.searchterm = searchTerm;
    if (reset) delete queryString.searchterm;

    setProvidersQueryString(queryString);
  };

  const handleSearchBarInputChange = event => {
    const { value } = event.target;

    if (value.length > 30) return;
    setSearchQuery(value);

    if (searchBarInputTimeout.current) clearTimeout(searchBarInputTimeout.current);

    if (value && value.length >= searchQueryMinLength) {
      searchBarInputTimeout.current = setTimeout(() => {
        clearTimeout(searchBarInputTimeout.current);

        handleSearchInputChange(value);
      }, 500);
    } else if (value.length < searchQueryMinLength && providersQueryString.searchterm)
      handleSearchInputChange(value, true);
  };

  const handleShowMoreClick = () => {
    const data = {
      ...providersQueryString,
    };
    data.pageNumber += 1;
    data.isShowMore = true;
    setProvidersQueryString(data);
  };

  const handleProviderDeactivate = rowDataPayload => {
    setPendingAction({ type: 'deactivate', data: rowDataPayload });
    setIsDeactivateConfirmationHidden(false);
  };

  const handleExclusiveToggle = rowDataPayload => {
    setPendingAction({ type: 'exclusive', data: rowDataPayload });
    setIsExclusiveConfirmationHidden(false);
  };

  const handleRemoveProvider = rowDataPayload => {
    setPendingAction({ type: 'remove', data: rowDataPayload });
    setIsRemoveConfirmationHidden(false);
  };

  const confirmDeactivate = () => {
    const { id, name, active, exclusive } = pendingAction.data;
    const payload = {
      id,
      data: {
        priceListId,
        name,
        active: !active,
        exclusive: exclusive || false,
      },
    };
    dispatch(editProvider(payload));
    setIsDeactivateConfirmationHidden(true);
    setPendingAction(null);
  };

  const confirmExclusive = () => {
    const { id, name, active, exclusive } = pendingAction.data;
    const payload = {
      id,
      data: {
        priceListId,
        name,
        active,
        exclusive: !exclusive,
      },
    };
    dispatch(editProvider(payload));
    setIsExclusiveConfirmationHidden(true);
    setPendingAction(null);
  };

  const confirmRemove = () => {
    const { id, priceListId } = pendingAction.data;
    const payload = {
      id,
      data: {
        priceListId,
      },
    };
    dispatch(removeProvider(payload));
    setIsRemoveConfirmationHidden(true);
    setPendingAction(null);
  };

  const cancelConfirmation = () => {
    setIsDeactivateConfirmationHidden(true);
    setIsExclusiveConfirmationHidden(true);
    setIsRemoveConfirmationHidden(true);
    setPendingAction(null);
  };

  const handleDropDownFilterChange = (fieldName, selectedValue) => {
    if (selectedValue) {
      setProvidersQueryString(prevValue => {
        const fieldsObj = { ...prevValue };
        fieldsObj[fieldName] = selectedValue;
        return fieldsObj;
      });
      setFiltersSelected(prevState => ({
        ...prevState,
        [fieldName]: true,
      }));
    } else {
      setProvidersQueryString(prevValue => {
        const fieldsObj = { ...prevValue };
        delete fieldsObj[fieldName];
        return fieldsObj;
      });
      setFiltersSelected(prevState => {
        const tempState = { ...prevState };
        delete tempState[fieldName];
        return tempState;
      });
    }
  };
  // ###############################################################
  // <--------------------- End UI Handlers ----------------------->
  // ###############################################################
  // ###############################################################
  // <-------------------------- Render --------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------ End Render ------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------- Return UI ------------------------->
  // ###############################################################
  return (
    <div>
      <SurgicalProvidersComponent
        isCreateProviderModalHidden={isCreateProviderModalHidden}
        setIsCreateProviderModalHidden={handleOnToggleAddProvidersModel}
        handleInputFieldChange={handleInputFieldChange}
        filtersSelected={filtersSelected}
        searchQuery={searchQuery}
        handleSearchBarInputChange={handleSearchBarInputChange}
        providersQueryString={providersQueryString}
        handleShowMoreClick={handleShowMoreClick}
        handleProviderDeactivate={handleProviderDeactivate}
        handleExclusiveToggle={handleExclusiveToggle}
        handleRemoveProvider={handleRemoveProvider}
        priceListId={priceListId}
        handleDropDownFilterChange={handleDropDownFilterChange}
        DropDownFilterChange={handleDropDownFilterChange}
      />

      {/* Confirmation Modals */}
      <ConfirmationModal
        title="Deactivate Provider"
        message={`Are you sure you want to ${pendingAction?.data?.active ? 'deactivate' : 'activate'} this provider?`}
        confirmText={pendingAction?.data?.active ? 'Deactivate' : 'Activate'}
        cancelText="Cancel"
        confirmButtonColor="#dc3545"
        onConfirm={confirmDeactivate}
        onCloseModal={cancelConfirmation}
        isModalHidden={isDeactivateConfirmationHidden}
        isLoading={editProviderState.status === status.FETCHING}
      />

      <ConfirmationModal
        title="Toggle Exclusive Status"
        message={`Are you sure you want to ${
          pendingAction?.data?.exclusive ? 'remove exclusive status from' : 'make exclusive'
        } this provider?`}
        confirmText={pendingAction?.data?.exclusive ? 'Remove Exclusive' : 'Make Exclusive'}
        cancelText="Cancel"
        confirmButtonColor="#007bff"
        onConfirm={confirmExclusive}
        onCloseModal={cancelConfirmation}
        isModalHidden={isExclusiveConfirmationHidden}
        isLoading={editProviderState.status === status.FETCHING}
      />

      <ConfirmationModal
        title="Remove Provider"
        message="Are you sure you want to remove this provider from the price list?"
        confirmText="Remove"
        cancelText="Cancel"
        confirmButtonColor="#dc3545"
        onConfirm={confirmRemove}
        onCloseModal={cancelConfirmation}
        isModalHidden={isRemoveConfirmationHidden}
        isLoading={removeProviderState.status === status.FETCHING}
      />
    </div>
  );
  // ###############################################################
  // <----------------------- End Return UI ----------------------->
  // ###############################################################
};

SurgicalProvidersContainer.propTypes = {};

SurgicalProvidersContainer.defaultProps = {};

export default SurgicalProvidersContainer;
